/**
 * Timestamp formatting pinned to the business timezone. Shared: the Amazon Ads
 * dashboard and CEO Pricing both print instants through here, so it lives in
 * `utils/` rather than inside either feature.
 *
 * ONLY FOR INSTANTS. Nothing else on the Amazon Ads dashboard goes through here,
 * and that is the whole point of the file — see the note below.
 *
 * ## What is a timestamp here, and what is not
 *
 * Almost every date on this screen is a REPORTING BOUNDARY, not a moment:
 * `meta.adWindow`, `meta.salesCoverage`, the weekly ASIN range, the business
 * report's period, the monthly search-term period, the chart's x-axis. They are
 * `DATE` columns in the service (`day`, `period_start`, `period_end`) and arrive
 * as bare `YYYY-MM-DD` strings with no time and no offset, because Amazon
 * reports a day as a day — "the week of 2026-07-04" is not an instant that
 * happened somewhere.
 *
 * Converting those to a timezone would be actively wrong. `new Date('2026-07-04')`
 * parses as UTC midnight, and rendering that in Central shifts it back to
 * 2026-07-03 — so a reporting week would display starting a day before the week
 * the figures actually cover, and the totals would silently disagree with the
 * dates printed above them. They are left exactly as the service sends them.
 *
 * `tbl_ads_account.last_ingest_at` is the one true instant: a `DATETIME`
 * recording when a sync actually ran. It is meaningless without a zone, and it
 * was previously rendered with `toLocaleString(undefined, …)` — the VIEWER's
 * zone, unlabelled — so the same sync read as 08:33 in Delhi and 22:03 in
 * Chicago, with nothing on screen to say which one you were looking at.
 *
 * ## Why the label is fixed
 *
 * `America/Chicago` is a named zone, so the platform handles the CST/CDT
 * daylight shift and the CLOCK TIME is always correct. The printed label is
 * pinned to "CST" rather than `Intl`'s daylight-aware short name (which renders
 * "CDT" from March to November), matching what the rest of the ERP already
 * does — `ArContactForm`'s `CENTRAL_TIME_LABEL`, `groupAuditLogs`, and
 * `salesKpi`'s `REPORT_TIME_ZONE`. One label across the app beats a technically
 * precise one that changes twice a year.
 */

/** Business timezone. Named, not a fixed offset, so daylight saving is the platform's problem. */
export const CENTRAL_TIME_ZONE = 'America/Chicago';

/** Printed zone label — fixed, see the note above on CST vs CDT. */
export const CENTRAL_TIME_LABEL = 'CST';

/** Shown when there is no timestamp, or one that cannot be parsed. */
export const UNKNOWN_TIMESTAMP = 'unknown';

/** Trailing `Z`, `+05:30` or `-0600` — i.e. the string already states its offset. */
const HAS_OFFSET = /(?:Z|[+-]\d{2}:?\d{2})$/i;

/**
 * Parse a server timestamp into a real instant.
 *
 * **A timestamp with no offset is read as UTC, not as the browser's local
 * time.** That is the one assumption in this file about the world outside it,
 * and it is the deployment's: the service and its MySQL run in UTC, the JDBC
 * URL pins `connectionTimeZone=SERVER&preserveInstants=true` against a UTC
 * database, and the DTO field is a `java.time.Instant`.
 *
 * Today that means the payload arrives as `2026-08-05T03:33:00Z` and this
 * normalisation does nothing. It matters if that ever stops being true — if the
 * field became a `LocalDateTime`, or another endpoint sent a bare MySQL
 * `DATETIME` — because the ECMAScript rule for a date-TIME string without an
 * offset is to treat it as LOCAL time. The reading would then be silently wrong
 * by the viewer's own offset (5.5 hours from India, 0 from London), which is
 * both invisible in testing done in one timezone and precisely the class of bug
 * this module exists to remove. Reading it as UTC matches where the value was
 * actually written.
 *
 * The space separator is normalised for the same reason: `2026-08-05 03:33:00`
 * is outside the spec's grammar, so browsers fall back to implementation-
 * defined parsing rather than agreeing on an answer.
 */
function parseServerInstant(iso: string): Date {
  const normalised = iso.trim().replace(' ', 'T');
  return new Date(HAS_OFFSET.test(normalised) ? normalised : `${normalised}Z`);
}

/*
 * `en-GB` for "5 Aug 2026, 08:33 AM" — day-first, which is the shape this line
 * already had via `dateStyle: 'medium'` + `timeStyle: 'short'`. Built once at
 * module load rather than per render: constructing an `Intl` formatter is
 * comparatively expensive and this banner re-renders on every filter change.
 *
 * `h12` rather than the `h23` this started with: a bare "05:32" is ambiguous to
 * a reader who has not been told the clock is 24-hour, and a sync that ran at
 * 5am then reads exactly like one that ran at 5pm — precisely the distinction
 * this line exists to make. The hour stays `2-digit` so a column of timestamps
 * lines up.
 */
const CENTRAL_TIMESTAMP_FORMAT = new Intl.DateTimeFormat('en-GB', {
  timeZone: CENTRAL_TIME_ZONE,
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h12',
});

/** Any whitespace character, including the exotic ones — see `renderTimestamp`. */
const ANY_SPACE = /\s/g;

/**
 * Assemble the formatted parts, with the day period upper-cased.
 *
 * Not `format()` plus a `.replace()`, because neither half of what needs fixing
 * is stable across platforms: `en-GB` renders the day period LOWERCASE ("pm"),
 * and some ICU versions separate it from the time with a narrow no-break space
 * rather than a plain one — invisible in a diff, but enough to make an equality
 * assertion pass on one machine and fail on another. Reading the parts names
 * the day period explicitly instead of pattern-matching a rendered string.
 */
function renderTimestamp(parsed: Date): string {
  return CENTRAL_TIMESTAMP_FORMAT.formatToParts(parsed)
    .map((part) => (part.type === 'dayPeriod' ? part.value.toUpperCase() : part.value))
    .join('')
    .replace(ANY_SPACE, ' ');
}

/**
 * An ISO instant → `5 Aug 2026, 08:33 AM CST`.
 *
 * Returns `UNKNOWN_TIMESTAMP` for a missing or unparseable value rather than
 * throwing or printing "Invalid Date": an account that has never synced has a
 * NULL `last_ingest_at`, which is a normal state and not an error.
 */
export function formatCentralTimestamp(iso: string | null | undefined): string {
  if (!iso) return UNKNOWN_TIMESTAMP;

  const parsed = parseServerInstant(iso);
  if (Number.isNaN(parsed.getTime())) return UNKNOWN_TIMESTAMP;

  return `${renderTimestamp(parsed)} ${CENTRAL_TIME_LABEL}`;
}
